/**
 * Heali Cart OOS Sync
 *
 * Watches the storefront cart and writes a `_oos_lines` cart attribute
 * encoding which product handles are out of stock per country. The Shopify
 * checkout-validation function (extension: heali-ca-validator) reads this
 * attribute and blocks checkout to those destinations, naming the specific
 * products in the error message.
 *
 * Attribute format: JSON object, country ISO → array of product handles.
 *   ""                                  → nothing OOS (Shopify deletes empty)
 *   '{"CA":["relax-headache-patch"]}'   → 1 item OOS in CA
 *   '{"CA":["a","b"],"US":["c"]}'       → split OOS across both markets
 *
 * Pairs with the ATC lock in sections/main-product.liquid — same Worker,
 * same `localOosOverrides` rules, so cart-time and PDP-time decisions stay
 * aligned.
 *
 * Install:
 *   1. Upload to your theme's `assets/` folder as `heali-cart-oos-sync.js`.
 *   2. In `layout/theme.liquid`, add (next to the existing _detected_country
 *      script):
 *        <script>
 *          window.HEALI_LOCAL_OOS_OVERRIDES = {
 *            ca: { 'relax-headache-patch': true },
 *            us: {}
 *          };
 *        </script>
 *        {{ 'heali-cart-oos-sync.js' | asset_url | script_tag }}
 *
 * Behavior:
 *   - Runs on page load (if cart is non-empty).
 *   - Re-runs after any /cart/add, /cart/change, /cart/update fetch call.
 *   - Re-runs when Shopify fires `cart:refresh` (theme convention).
 *   - Caches Worker responses in sessionStorage to keep it cheap.
 *   - Fails open: if Worker errors, attribute is cleared, function falls
 *     through and allows the order.
 *
 * Migration note: previous version wrote `_oos_countries` as comma-separated
 * country list. This version writes `_oos_lines` as JSON and also clears the
 * deprecated `_oos_countries` attribute so old data doesn't linger.
 */

(function () {
  'use strict';

  var WORKER_URL = 'https://heali-inventory.philip-56e.workers.dev/';
  // Markets to probe. Match exactly the lowercase market handles the Worker
  // accepts. Add more here if/when Heali expands.
  var MARKETS = [
    { handle: 'ca', country: 'CA' },
    { handle: 'us', country: 'US' },
  ];
  var CACHE_PREFIX = 'heali_oos_v2__'; // bumped from v1 (attribute format changed)
  var ATTR_KEY = '_oos_lines';
  var LEGACY_ATTR_KEY = '_oos_countries'; // cleared on every write
  var DEBUG = false;

  function log() {
    if (!DEBUG) return;
    try {
      console.log.apply(console, ['[heali-oos]'].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  function getOverrides() {
    var o = window.HEALI_LOCAL_OOS_OVERRIDES;
    if (!o || typeof o !== 'object') return {};
    return o;
  }

  function overrideSaysOos(market, productHandle) {
    if (window.healiIsLocalOosOverride) {
      try {
        return !!window.healiIsLocalOosOverride(market, productHandle);
      } catch (e) {
        return false;
      }
    }
    var o = getOverrides();
    return !!(o[market] && o[market][productHandle]);
  }

  function cacheKey(variantId, market) {
    return CACHE_PREFIX + variantId + '__' + market;
  }

  function cacheGet(variantId, market) {
    try {
      var v = sessionStorage.getItem(cacheKey(variantId, market));
      if (v === '1') return true;
      if (v === '0') return false;
    } catch (e) {}
    return null;
  }

  function cacheSet(variantId, market, available) {
    try {
      sessionStorage.setItem(cacheKey(variantId, market), available ? '1' : '0');
    } catch (e) {}
  }

  /**
   * Returns Promise<boolean>: true = variant is available in market, false = OOS.
   * Treats Worker error as available=true (fail-open).
   */
  function checkVariantInMarket(variantId, market, productHandle) {
    if (overrideSaysOos(market, productHandle)) {
      return Promise.resolve(false);
    }
    var cached = cacheGet(variantId, market);
    if (cached !== null) return Promise.resolve(cached);

    var url = WORKER_URL + '?variant_id=' + encodeURIComponent(variantId) +
              '&market=' + encodeURIComponent(market);
    return fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : { available: true }; })
      .then(function (data) {
        var ok = data && data.available !== false;
        cacheSet(variantId, market, ok);
        return ok;
      })
      .catch(function () {
        // Fail open on network error — don't block real customers because
        // the Worker had a hiccup.
        return true;
      });
  }

  /**
   * Given a cart (from /cart.js), compute the per-country list of OOS
   * product handles. Returns Promise<{CA: ["handle1"], US: []}>.
   * Always includes a key for each MARKET (even with empty array), so the
   * downstream caller can distinguish "no data" (empty object) from
   * "everything fine" (object with empty arrays).
   */
  function computeOosLines(cart) {
    var items = (cart && cart.items) || [];
    var result = {};
    MARKETS.forEach(function (m) { result[m.country] = []; });
    if (items.length === 0) return Promise.resolve(result);

    var probes = [];
    MARKETS.forEach(function (m) {
      items.forEach(function (item) {
        probes.push(
          checkVariantInMarket(item.variant_id, m.handle, item.handle)
            .then(function (available) {
              return {
                country: m.country,
                handle: item.handle,
                available: available,
              };
            })
        );
      });
    });

    return Promise.all(probes).then(function (results) {
      results.forEach(function (r) {
        if (!r.available && result[r.country].indexOf(r.handle) === -1) {
          result[r.country].push(r.handle);
        }
      });
      return result;
    });
  }

  function readCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  /**
   * Serialize and write the per-country OOS map. Filters out countries with
   * empty arrays so the JSON only contains actionable data. Empty string is
   * Shopify's "delete attribute" sentinel — we lean on that when there's
   * nothing OOS.
   */
  function writeOosAttribute(oosLines) {
    var nonEmpty = {};
    Object.keys(oosLines).forEach(function (country) {
      var handles = oosLines[country];
      if (Array.isArray(handles) && handles.length > 0) {
        // Sort for stable JSON output (avoids unnecessary cart writes when
        // the same set comes back in a different order).
        nonEmpty[country] = handles.slice().sort();
      }
    });
    var value = Object.keys(nonEmpty).length === 0
      ? ''
      : JSON.stringify(nonEmpty);

    // Skip the write if nothing changed (cuts cart-traffic noise).
    if (window.__healiLastOosWrite === value) {
      log('skip write, unchanged:', JSON.stringify(value));
      return Promise.resolve();
    }
    window.__healiLastOosWrite = value;
    log('writing _oos_lines =', JSON.stringify(value));

    // Always clear the legacy _oos_countries attribute on the same call so
    // we don't leave stale data behind from the v1 of this script.
    var attributes = {};
    attributes[ATTR_KEY] = value;
    attributes[LEGACY_ATTR_KEY] = '';

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: attributes }),
    });
  }

  var syncInFlight = false;
  var syncQueued = false;

  function sync() {
    if (syncInFlight) { syncQueued = true; return; }
    syncInFlight = true;
    readCart()
      .then(computeOosLines)
      .then(writeOosAttribute)
      .catch(function (e) {
        log('sync error', e);
      })
      .then(function () {
        syncInFlight = false;
        if (syncQueued) { syncQueued = false; sync(); }
      });
  }

  // -- Hook into cart mutations ----------------------------------------------
  // Intercept fetch() to re-sync after any cart-mutating endpoint resolves.
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string'
        ? input
        : (input && input.url) || '';
      var p = origFetch.apply(this, arguments);
      if (CART_MUTATION_RE.test(url)) {
        p.then(function () { sync(); }, function () { sync(); });
      }
      return p;
    };
  }

  // Some themes fire a "cart:refresh" or "cart:updated" event after mutations.
  // Listen for both, harmless if the theme never fires them.
  ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (evt) {
    document.addEventListener(evt, sync);
  });

  // Initial sync on page load (covers the case where the cart already has
  // items from a prior session).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();
